import React, { useState } from 'react';
import { EditSpeakOptions } from './EditSpeakOptions';
import { ipaToSynth } from '../speak/ipaToSynth';
import { speakEspeak, speakPhonemes, stopEspeak } from '../speak/speakPhonemes';
import { ChoiceList } from './widgets/ChoiceList';
import { InputLanguageOptions, PhonemeShifter } from './PhonemeShifter';
import { RadioList } from './widgets/RadioList';
import { SystemSpeech } from './SystemSpeech';
import { convertSynthToIpa } from '../speak/convertSynthToIpa';
import { loadEnglishDictionaries } from '../loader/loadEnglishDictionaries';
import { IpaKeyboard } from './widgets/IpaKeyboard';
import { speakOnCtrlS } from './widgets/SpeakOnCtrlS';

export interface RunRulesProps {
  readonly original: string;
}

export function RunRulesArea(props: RunRulesProps) {
  const [shifter] = useState(() => new PhonemeShifter());
  const [inputLanguage, setInputLanguage] = useState('American English');
  const [languageText, setLanguageText] = useState(props.original);
  const [rulesName, setRulesName] = useState(shifter.getRuleNames()[0]);
  const [sampleText, setSampleText] = useState(englishSentences);
  const [badLanguage, setBadLanguage] = useState('');
  const [ipaInput, setIpaInput] = useState('loading dictionaries...');
  const [badIpa, setBadIpa] = useState('');
  const [output, setOutput] = useState('');
  const [badOutput, setBadOutput] = useState('');
  const [approxIpa, setApproxIpa] = useState('');
  const [espeak, setEspeak] = useState('');
  const [init, setInit] = useState(false);

  if (!init) {
    setInit(true);
    loadEnglishDictionaries()
      .then(() => {
        const { ipa } = shifter.transformLanguage(props.original, shifter.getRuleNames()[0]);
        setIpaInput(ipa);
        const { afterRules } = shifter.transformLanguage(props.original, shifter.getRuleNames()[0]);
        setOutput(afterRules);
      })
      .catch(() => {
        setIpaInput('failed to load dictionaries');
      });
  }

  function runRulesOnIpa(rulesName: string, input: string) {
    const { afterRules, badIpa } = shifter.runRuleOnIpa({ ipa: input }, rulesName);
    setBadIpa(badIpa ? badIpa.join(' ') : '');
    setOutput(afterRules);
  }

  function enterLanguageText(sentence: string) {
    const { ipa, afterRules, badLanguage, badIpa } = shifter.transformLanguage(sentence, rulesName);
    setLanguageText(sentence);
    setBadLanguage(badLanguage ? badLanguage.join(' ') : '');
    setIpaInput(ipa);
    setBadIpa(badIpa ? badIpa.join(' ') : '');
    setOutput(afterRules);
  }

  function speak(input: string, withRules: boolean) {
    const addPhonemeLength = shifter.getAddPhonemeLength(withRules ? rulesName : true);
    const { asSynth, unsupported } = speakPhonemes(input, undefined, addPhonemeLength);
    setBadOutput(unsupported ? unsupported.join(' ') : '');

    // show synth with lengths & approximate IPA without modified phoneme lengths
    setEspeak(asSynth);
    const synth = ipaToSynth(input);
    const [backAgain] = convertSynthToIpa(synth.speak);
    const ipa = backAgain.join('');
    setApproxIpa(ipa);
  }
  function speakInput() {
    speak(ipaInput, false);
  }
  function speakOutput() {
    speak(output, true);
  }

  function runCurrentRulesOnIpa() {
    runRulesOnIpa(rulesName, ipaInput);
  }

  function changeRules(ruleName: string) {
    setRulesName(ruleName);
    const { afterRules } = shifter.applyRule({ language: languageText, ipa: ipaInput }, ruleName);
    setOutput(afterRules);
  }

  function changeInputLanguage(language: string) {
    setInputLanguage(language);
    const sentences = languageSentences[language];
    setSampleText(sentences ?? []);
    shifter.setLanguage(languageLookup[language]);
  }

  return (<>
    <div style={{ display: 'grid', gridTemplateColumns: 'auto 30%' }}>
      <div>
        <p>
          input: <ChoiceList items={inputLanguages} selectedItem={inputLanguage} onSelect={changeInputLanguage} />
          &nbsp;&nbsp;
          output: IPA
        </p>
        <ChoiceList items={sampleText} selectedItem={languageText} onSelect={enterLanguageText} />
        <br /><br />
        <textarea cols={80} rows={5} spellCheck={false} value={languageText}
          onChange={(foo) => enterLanguageText(foo.target.value)}
          onKeyDown={e => speakOnCtrlS(e, speakOutput)} />
        <br />{badLanguage === '' ? '' : 'unknown: ' + badLanguage}

        <br />IPA version of input<br />
        <IpaKeyboard value={ipaInput} onChange={setIpaInput} onSpeak={speakInput} cols={80} rows={5} />
        <br />{badIpa === '' ? '' : 'unknown: ' + badIpa}

        <br />IPA with rules applied<br />
        <IpaKeyboard value={output} onChange={setOutput} onSpeak={speakOutput} cols={80} rows={5} />
        <br />{badOutput === '' ? '' : 'unknown: ' + badOutput}

        <br />
        <button onClick={speakInput}>speak IPA</button>
        &nbsp;&nbsp;
        <button onClick={speakOutput}>speak output</button>
        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
        <button onClick={() => speakEspeak(espeak)}>speak espeak</button>
        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
        <button onClick={() => stopEspeak()}>stop</button>
        <br />
        <SystemSpeech input={languageText} />

        <br /><br /><br />espeak phonemes<br />
        <textarea cols={80} rows={5} spellCheck={false} value={espeak}
          onChange={(event) => setEspeak(event.target.value)}
          onKeyDown={e => speakOnCtrlS(e, () => speakEspeak(espeak))} />
        <br />pronounced as (IPA)<br />
        <textarea cols={80} rows={5} spellCheck={false} value={approxIpa} readOnly={true} />
      </div>
      <div>
        <button onClick={runCurrentRulesOnIpa}>run rules on IPA</button>
        <br /><br />
        <RadioList items={shifter.getRuleNames()} selectedItem={rulesName} onSelect={changeRules} />
      </div>
    </div>
    <hr />
    <EditSpeakOptions />
    <br /><br />
  </>
  );
}

const inputLanguages = [
  'English (US - General)',
  'English (UK - RP)',
  'English (no dictionary)',
  'Old English',
  'Spanish',
  'Lushootseed',
  'Americanist phonetic notation',
  'Sanskrit (IAST)',
  'CXS (Conlang X-SAMPA)',
]
const languageLookup: Record<string, InputLanguageOptions> = {
  'English (US - General)': 'US',
  'English (UK - RP)': 'UK',
  'English (no dictionary)': 'US rules',
  'Old English': 'Old English',
  'Spanish': 'Spanish',
  'Lushootseed': 'Lushootseed',
  'Americanist phonetic notation': 'Americanist',
  'Sanskrit (IAST)': 'Sanskrit (IAST)',
  'CXS (Conlang X-SAMPA)': 'CXS',
};

const englishSentences = [
  "",
  "good day mate",
  "what have we here",
  "I put my foot in my mouth",
  "what did you do that for",
  "he's teaching me to walk talk and act like a regular lady",
  "one two three four five six seven eight nine ten",
];

const oldEnglishSentences = [
  "",
  // sections of Beowulf from https://heorot.dk/beo-ru.html
  "Hwæt. Wé Gárdena, in géardagum, þéodcyninga þrym gefrúnon, hú ðá æþelingas, ellen fremedon.",
  "Oft Scyld Scéfing, sceaþena þréatum, monegum maégþum, meodosetla oftéah, egsode Eorle,",
  "syððan aérest wearð, féasceaft funden, hé þæs frófre gebád, wéox under wolcnum, weorðmyndum þáh,",
  "oð þæt him aéghwylc, þára ymbsittendra, ofer hronráde, hýran scolde, gomban gyldan, þæt wæs gód cyning.",
  "nolde eorla hléo aénige þinga þone cwealmcuman cwicne forlaétan",
];

const spanishSentences = [
  "",
  "Don Quijote de la Mancha es una novela escrita por el español Miguel de Cervantes Saavedra.",
];

const lushootseedSentences = [
  "",
  // from https://en.wikipedia.org/wiki/Lushootseed
  "sqʷəbayʔ ti ʔučalatəb ʔə tiʔiɬ ˈwiwʼsu",  // The dog is what the children chased
  "šəqabac ʔal ti ˈpiit", // On top of the bed
  "č̓uʔ , saliʔ , ɬixʷ , buus , cəlac , yəlaʔc , c̓uʔkʷs , təqačiʔ , x̌ʷəl , ʔulub", // 1-10
]

const americanistSentences = [
  "",
  "Sqʷx̣ʷúʔməš",
  "Nəxʷsƛ̕áy̓emúcən",
];

const sanskritSentences = [
  "",
  "Sarvē mānavāḥ svatantrāḥ samutpannāḥ vartantē api ca,",
  "gauravadr̥śā adhikāradr̥śā ca samānāḥ ēva vartantē.",
];

const cxsSentences = [
  "",
  "k@m'plEksIti",
]

const languageSentences: Record<string, string[]> = {
  'English (US - General)': englishSentences,
  'English (UK - RP)': englishSentences,
  'English (no dictionary)': englishSentences,
  'Old English': oldEnglishSentences,
  'Spanish': spanishSentences,
  'Lushootseed': lushootseedSentences,
  'Americanist phonetic notation': americanistSentences,
  'Sanskrit (IAST)': sanskritSentences,
  'CXS (Conlang X-SAMPA)': cxsSentences,
};
